// English mirror of pt-BR.ts. The `Dict` annotation makes a missing key a
// compile error; tests/i18n.test.ts additionally catches extra keys and empty
// values. Keep the key order identical to pt-BR.ts so diffs stay reviewable.

import type { Dict } from './pt-BR.ts';

export const en: Dict = {
  // ── Common ────────────────────────────────────────────────────────────────
  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.back': 'Back',
  'common.more': 'More',
  'common.moreAria': 'More options',
  'common.on': 'On',
  'common.off': 'Off',

  // ── Menu groups ───────────────────────────────────────────────────────────
  'menu.group.navegar': 'Navigate',
  'menu.group.ver': 'View',
  'menu.group.compartilhar': 'Share',
  'menu.group.acessibilidade': 'Accessibility',
  'menu.group.ferramentas': 'Tools',

  // ── Menu entries ──────────────────────────────────────────────────────────
  'menu.floorplan.label': 'Floor plan',
  'menu.floorplan.aria': 'Show or hide the floor plan',
  'menu.floorplan.title': 'Floor plan (F)',

  'menu.rooms.label': 'Rooms',
  'menu.rooms.aria': 'Show or hide the room list',
  'menu.rooms.title': 'Rooms (F)',

  'menu.dollhouse.label': 'Dollhouse',
  'menu.dollhouse.aria': 'Show or hide the 3D dollhouse',
  'menu.dollhouse.title': '3D dollhouse (M)',

  'menu.variants.label': 'Styles',
  'menu.variants.aria': 'Choose a design style',
  'menu.variants.count': 'Styles ({current}/{total})',

  'menu.sun.label': 'Time of day',
  'menu.sun.aria': 'Adjust the time of day',
  'menu.sun.title': 'Time of day (S)',

  'menu.share.label': 'Link',
  'menu.share.aria': 'Copy a shareable link to this view',
  'menu.share.title': 'Copy link with preview (L)',

  'menu.qr.label': 'QR code',
  'menu.qr.aria': 'Show a QR code for the current view',
  'menu.qr.title': 'QR code for the current view (Q)',

  'menu.whatsapp.label': 'WhatsApp',
  'menu.whatsapp.aria': 'Share via WhatsApp',
  'menu.whatsapp.title': 'Send this apartment via WhatsApp',

  'menu.chat.label': 'Assistant',
  'menu.chat.aria': 'Chat with the assistant',
  'menu.chat.title': 'Ask about the room, materials or how to get around',

  'menu.gallery.label': 'Gallery',
  'menu.gallery.aria': 'Styles you have explored',
  'menu.gallery.title': 'Gallery of AI-generated styles',

  'menu.savedSession.label': 'Save',
  'menu.savedSession.aria': 'Save my session',
  'menu.savedSession.title': 'Save your session — come back exactly where you left off',

  'menu.narrator.label': 'Narration',
  'menu.narrator.aria': 'Turn on the narrated tour',
  'menu.narrator.ariaOff': 'Turn off the narrated tour',
  'menu.narrator.title': 'Narrated tour',

  'menu.autotour.label': 'Auto tour',
  'menu.autotour.aria': 'Start the cinematic tour',
  'menu.autotour.title': 'Cinematic tour — the camera walks through every room',
  'menu.autotourStop.label': 'Stop tour',
  'menu.autotourStop.aria': 'Stop the cinematic tour',

  'menu.audio.label': 'Sound',
  'menu.audio.aria': 'Turn on ambient sound',
  'menu.audio.ariaOff': 'Turn off ambient sound',

  'menu.voice.label': 'Voice',
  'menu.voice.aria': 'Voice command',
  'menu.voice.title': 'Voice command · e.g. "go to the kitchen", "before and after"',

  'menu.tilt.label': 'Tilt',
  'menu.tilt.aria': 'Explore by tilting your device',
  'menu.tilt.title': 'Tilt your device to look around',

  'menu.vr.label': 'Enter VR',
  'menu.vr.labelExit': 'Exit VR',
  'menu.vr.aria': 'Enter virtual reality mode',

  'menu.language.label': 'Language',
  'menu.language.aria': 'Choose a language',

  // Operator-only (behind ?operator=1)
  'menu.measure.label': 'Measure',
  'menu.measure.aria': 'Measure distances',
  'menu.note.label': 'Note',
  'menu.note.aria': 'Leave a note in this room',
  'menu.report.label': 'Report',
  'menu.report.aria': 'Download the tour report as a PDF',
  'menu.copresence.label': 'Invite',
  'menu.copresence.aria': 'Invite someone to a shared tour',
  'menu.follow.label': 'Follow',
  'menu.follow.aria': "Follow the other participant's view",
  'menu.dashboard.label': 'Insights',
  'menu.dashboard.aria': 'Open the session insights',
  'menu.debug.label': 'Debug',
  'menu.debug.aria': 'Show the yaw and pitch readout',

  // ── Bottom bar ────────────────────────────────────────────────────────────
  'bottombar.beforeAfter': 'Before / After',
  'bottombar.compare': 'Compare',
  'bottombar.compareTitle': 'Take a snapshot and drag to compare it with the Before style',
  'bottombar.variantsAria': 'Design styles',

  // ── Topbar ────────────────────────────────────────────────────────────────
  'topbar.eyebrow': 'Room',

  // ── Language sheet ────────────────────────────────────────────────────────
  'language.pt-BR': 'Português (BR)',
  'language.en': 'English',
};
